import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import useAuth from "../Hooks/useAuth";
import usePermission from "../Hooks/usePermission";
import Alerts from "../Components/Utils/Alert";
import BillList from "../Components/Bill/BillList";
import useBillOperationData from "../Hooks/useBillOperationData";
import { useFetchProductTypeQuery } from "../store";
import {
  PageShell,
  PageHeader,
  FilterTabs,
  SearchInput,
  ListSkeleton,
  EmptyState,
} from "../Components/shared";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { TbReportAnalytics } from "react-icons/tb";
import {
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLayerGroup,
} from "react-icons/fa";
import * as XLSX from "xlsx";

const BILL_FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "valid", label: "Yüklenebilir", icon: FaCheckCircle },
  { id: "error", label: "Hatalı", icon: FaExclamationTriangle },
];

/**
 * Faturanın ürün tipi. Excel'den gelen `productName` serbest metindir
 * ("MALZEME AÇIKLAMASI"), tipe göre gruplamak için güvenilir değil; server
 * kontrol adımında ürün koduna karşılık gelen kanonik tipi `productTypeName`
 * olarak döner (bkz. bill-storage.controller.js). Tip çözülemeyen faturalar
 * (ürün kodu tanımsız → zaten "Hatalı") filtre dışında kalır.
 */
const billProductType = (bill) => bill?.productTypeName || null;

const TABLE_COLUMNS = [
  { label: "Fatura No", width: "w-36", skeletonWidth: "w-24" },
  { label: "Firma Bilgileri", width: "", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "Ürün Bilgileri", width: "", skeletonWidth: "w-28", skeletonExtra: true },
  { label: "Finansal Detaylar", width: "w-48", skeletonWidth: "w-36" },
  { label: "Durum", width: "w-28", skeletonWidth: "w-16" },
];

const convertExcelDate = (excelDate) => {
  if (!excelDate || isNaN(excelDate)) return excelDate;
  const excelEpoch = new Date(1900, 0, 1);
  const msPerDay = 86400000;
  const adjusted = excelDate > 59 ? excelDate - 1 : excelDate;
  return new Date(excelEpoch.getTime() + (adjusted - 1) * msPerDay).toISOString();
};

function BillPage() {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("bills.create");
  const canControll = hasPermission("bills.controll");
  const token = auth.accessToken;
  const hiddenFileInput = useRef(null);

  const [activeFilter, setActiveFilter] = useState("all");
  const [activeProduct, setActiveProduct] = useState("all");
  const [searchBillNo, setSearchBillNo] = useState("");
  const [searchFirm, setSearchFirm] = useState("");
  const [searchBayser, setSearchBayser] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Urun tipleri API'den — filtre butonlari Excel yuklenmeden de dolu olsun.
  // Fatura verisinden turetmek yeterli degildi: dosya yuklenmeden liste bos
  // kaliyordu ve kullanicinin filtreleri gormesi gerekiyor.
  // Uç products.typeRead ister — izni yoksa istek atılmaz, filtreler
  // fatura verisinden türetilir (#414)
  const productTypeResult = useFetchProductTypeQuery(token, {
    skip: !hasPermission("products.typeRead"),
  });

  const {
    alert,
    message,
    state,
    setState,
    resultCreate,
    controll,
    resultControll,
    HandleUpload,
    sortData,
  } = useBillOperationData(token);

  const isLoading = resultControll?.isLoading || resultCreate?.isLoading;
  const hasData = resultControll?.isSuccess && resultControll?.data?.results > 0;

  const rawBills = useMemo(() => {
    if (state === 2) return resultCreate?.data?.bills || [];
    if (state === 1) return resultControll?.data?.bills || [];
    return [];
  }, [state, resultCreate?.data, resultControll?.data]);

  const filteredBills = useMemo(() => {
    let data = rawBills;

    if (activeFilter === "valid") data = data.filter((b) => b.status === true);
    else if (activeFilter === "error") data = data.filter((b) => !b.status);

    if (activeProduct !== "all") {
      data = data.filter((b) => billProductType(b) === activeProduct);
    }

    const q1 = searchBillNo.toLowerCase();
    const q2 = searchFirm.toLowerCase();
    const q3 = searchBayser.toLowerCase();

    if (q1 || q2 || q3) {
      data = data.filter((b) => {
        if (q1 && !b.billNo?.toString().toLowerCase().includes(q1)) return false;
        if (q2 && !b.firmApellation?.toString().toLowerCase().includes(q2)) return false;
        if (q3 && !b.bayserNo?.toString().toLowerCase().includes(q3)) return false;
        return true;
      });
    }

    return sortData(data, { key: null, direction: "ascending" });
  }, [rawBills, activeFilter, activeProduct, searchBillNo, searchFirm, searchBayser, sortData]);

  /**
   * Ürün tipi filtreleri — kaynak sistemde tanımlı ürün tipleri (Çay, Filtre
   * Kahve, Türk Kahvesi, Espresso...), fatura verisi değil. Böylece butonlar
   * Excel yüklenmeden de görünür ve yeni bir tip tanımlandığında sabit listeyi
   * güncellemek gerekmez. Sayaçlar yüklü faturalardan gelir; henüz veri yoksa
   * sayaç gösterilmez.
   */
  const productFilters = useMemo(() => {
    const counts = new Map();
    rawBills.forEach((bill) => {
      const type = billProductType(bill);
      if (!type) return;
      counts.set(type, (counts.get(type) || 0) + 1);
    });

    const types = (
      productTypeResult?.data?.data?.productTypes || []
    )
      .map((t) => t?.productTypeName)
      .filter(Boolean);

    // Faturada olup katalogda olmayan tip düşmesin (ör. tip sonradan silinmiş)
    counts.forEach((_, type) => {
      if (!types.includes(type)) types.push(type);
    });

    if (types.length === 0) return [];
    return [
      { id: "all", label: "Tüm Ürünler", icon: FaLayerGroup },
      ...types.map((type) => ({
        id: type,
        label: type,
        ...(counts.has(type) ? { count: counts.get(type) } : {}),
      })),
    ];
  }, [rawBills, productTypeResult?.data]);

  // Yeni dosyada seçili tip yoksa filtre sessizce boş liste göstermesin
  useEffect(() => {
    if (
      activeProduct !== "all" &&
      !productFilters.some((f) => f.id === activeProduct)
    ) {
      setActiveProduct("all");
    }
  }, [productFilters, activeProduct]);

  const stats = useMemo(() => {
    const total = rawBills.length;
    const valid = rawBills.filter((b) => b.status === true).length;
    const errors = rawBills.filter((b) => !b.status).length;
    return { total, valid, errors };
  }, [rawBills]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, activeProduct, searchBillNo, searchFirm, searchBayser]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const readExcel = useCallback(
    (file) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const wb = XLSX.read(e.target.result, { type: "buffer" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const headers = rows[0];
        const bills = rows.slice(1).map((row) => {
          const obj = {};
          headers.forEach((h, i) => (obj[h] = row[i]));
          return {
            bayserNo: obj["BAYSER NO"],
            firmApellation: obj["CARİ HESAP ÜNVANI"],
            billNo: obj["FİŞ NUMARASI"],
            createdInfo: convertExcelDate(obj["TARİHİ"]),
            shippingInfo: obj["SEVK AÇIKLAMA"],
            productName: obj["MALZEME AÇIKLAMASI"],
            productCode: obj["MALZEME KODU"],
            price: obj["SATIR TOPLAMI"] / obj["SATIR MİKTARI"],
            quota: obj["SATIR MİKTARI"],
            income: obj["SATIR TOPLAMI"],
          };
        });
        setState(1);
        controll({ bills, token });
      };
    },
    [controll, setState, token]
  );

  const headerStats = useMemo(
    () => [
      {
        icon: FaLayerGroup,
        value: stats.total,
        label: "Toplam",
        color: "blue",
      },
      {
        icon: FaCheckCircle,
        value: stats.valid,
        label: "Yüklenebilir",
        color: "emerald",
      },
      {
        icon: FaExclamationTriangle,
        value: stats.errors,
        label: "Hatalı",
        color: "amber",
      },
    ],
    [stats]
  );

  const headerActions = useMemo(
    () => [
      ...(canControll
        ? [
            {
              label: "Excel Yükle",
              icon: AiOutlineCloudUpload,
              variant: "primary",
              onClick: () => {
                setState(0);
                hiddenFileInput.current.click();
              },
            },
          ]
        : []),
      ...(canCreate
        ? [
            {
              label: "Veritabanına Kaydet",
              icon: TbReportAnalytics,
              variant: "success",
              disabled: !hasData || state === 2,
              onClick: HandleUpload,
            },
          ]
        : []),
    ],
    [hasData, state, HandleUpload, setState, canCreate, canControll]
  );

  return (
    <PageShell>
      {alert !== 0 && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex justify-center pt-4 px-4">
          <div className="pointer-events-auto">
            <Alerts AlertChoice={alert} message={message} />
          </div>
        </div>
      )}

      <input
        type="file"
        ref={hiddenFileInput}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) readExcel(file);
          e.target.value = "";
        }}
        accept=".xlsx,.xls"
        className="hidden"
      />

      {/* Header */}
      <div className="px-3 py-2.5 sm:px-5 sm:py-3 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaFileInvoiceDollar}
          iconColor="amber"
          title="Fatura Yükle"
          subtitle="Excel yükleme, kontrol ve onaylama"
          stats={hasData ? headerStats : []}
          actions={headerActions}
          isLoading={isLoading}
        />
      </div>

      {/* Filters & Search — veri yuklenmeden de gorunur; kullanici sayfaya
          girdiginde arama/filtre araclarini gormeli. Sayac yalnizca veri
          varken anlamli oldugu icin totalCount o zaman geciliyor. */}
      <div className="px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-100 shrink-0 space-y-3">
        <FilterTabs
          filters={BILL_FILTERS}
          activeFilter={activeFilter}
          onChange={setActiveFilter}
          totalCount={hasData ? filteredBills.length : undefined}
          isLoading={isLoading}
          disabled={!hasData}
          color="amber"
        />

        {productFilters.length > 0 && (
          <FilterTabs
            filters={productFilters}
            activeFilter={activeProduct}
            onChange={setActiveProduct}
            totalCount={hasData ? filteredBills.length : undefined}
            isLoading={isLoading}
            disabled={!hasData}
            color="emerald"
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
          <SearchInput
            placeholder="Fatura No ara..."
            value={searchBillNo}
            onChange={(e) => setSearchBillNo(e.target.value)}
            disabled={!hasData}
          />
          <SearchInput
            placeholder="Firma ünvanı ara..."
            value={searchFirm}
            onChange={(e) => setSearchFirm(e.target.value)}
            disabled={!hasData}
          />
          <SearchInput
            placeholder="Bayser No ara..."
            value={searchBayser}
            onChange={(e) => setSearchBayser(e.target.value)}
            disabled={!hasData}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {isLoading ? (
          <ListSkeleton columns={TABLE_COLUMNS} rowCount={10} />
        ) : !hasData ? (
          <EmptyState
            message="Fatura Bulunamadı"
            suggestion="Lütfen Excel dosyası yükleyin."
          />
        ) : filteredBills.length === 0 ? (
          <EmptyState
            message="Eşleşen fatura bulunamadı"
            suggestion="Filtreleri veya arama terimlerini değiştirin."
          />
        ) : (
          <BillList
            bills={filteredBills}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            state={state}
          />
        )}
      </div>
    </PageShell>
  );
}

export default BillPage;
